import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subscription } from 'rxjs';
import { APICallService } from 'src/app/services/api-call.service';
import { AppMessageService } from 'src/app/services/app-message.service';
import { FormService } from 'src/app/services/form.service';
import { JsonDataService } from 'src/app/services/json-data.service';
import { SecurityService } from 'src/app/services/security.service';
import { UserDetailsService } from 'src/app/services/user-details.service';
import { ClientGroupLovDialog } from '../../common/client-group-lov-dialog/client-group-lov-dialog.component';
import { DialogMsgComponent } from '../../common/dialog-msg/dialog-msg.component';
import { LeaveConfirmation } from '../../common/guard/check.guard';
import { LineLovDialogComponent } from '../../common/line-lov-dialog/line-lov-dialog.component';

@Component({
  selector: 'towing-amt-per-client-grp-maintenance',
  templateUrl: './towing-amt-per-client-grp-maintenance.component.html',
  styleUrls: ['./towing-amt-per-client-grp-maintenance.component.css']
})
export class TowingAmtPerClientGrpMaintenanceComponent implements OnInit, LeaveConfirmation, OnDestroy {

  private moduleId: string = 'BMM155';
  public moduleName: string = this._jDS.data.moduleList.find((a: any) => a.moduleId === this.moduleId)?.moduleDesc ?? "";
  private datePipe = new DatePipe('en-us');
  private valueChangesSubscriptions: Subscription = new Subscription();

  constructor(
    public _formService: FormService,
    private fb: FormBuilder,
    private _matDialog: MatDialog,
    private _api: APICallService,
    private _jDS: JsonDataService,
    private _userDetailService: UserDetailsService,
    private _securityService: SecurityService,
    private _appMessageService: AppMessageService,
  ) {
    this.userIdLoggedIn = this._userDetailService.userId ?? "";
  }

  public canDeactivate(): boolean | Observable<boolean> {
    return !(this.defaultTowingLimitTblConfig.tblData?.filter((row: any) => ['I', 'U'].includes(row.operation)).length > 0);
  }

  public btns = {
    defaultTowingLimit: {
      updateDisabled: true,
      deleteDisabled: true,
      saveDisabled: true,
      addDisabled: true,
    },
    filterLov: {
      sublineSearchDisabled: false,
      clientGrpSearchDisabled: false,
    }
  };

  public showForm: boolean = true;

  public maxEffDate!: Date | undefined;
  public minExpDate!: Date | undefined;

  //* Data Stuffs */
  private rowData: any;
  private errorMessage: string = "";
  private userIdLoggedIn: string;
  //? set selectedRow to [] to unselect the table
  public selectedRow!: any[];

  public forms = {
    filterLovForm: this.fb.group({
      lineCd: [null],
      sublineCd: [null],
      sublineName: [null],
      clientCd: [null],
      clientName: [null],
      sublineCdDisp: [{ value: '', disabled: 'true' }],
      clientCdDisp: [{ value: '', disabled: 'true' }],
    }),
    defaultTowingLimitForm: this.fb.group({
      lineCd: [null],
      sublineCd: [null],
      clientCd: [null],
      towingAmt: [null, [Validators.required, this.checkDecimalsExceeded(14, 2)]],
      active: ["A", [Validators.required]],
      effDateFrom: [new Date(), [Validators.required]],
      effDateTo: [null],
      remarks: [null],
      lastUpdateUser: [{ value: '', disabled: 'true' }],
      lastUpdate: [{ value: '', disabled: 'true' }],
    }, {
      validators: [this.checkDateValid()],
    }),
    resetDefaultTowingLimitForm: () => {
      this.forms.defaultTowingLimitForm.reset();
      this.btns.defaultTowingLimit.updateDisabled = true;
      this.btns.defaultTowingLimit.deleteDisabled = true;

      this.forms.defaultTowingLimitForm.patchValue({
        active: 'A',
        effDateFrom: new Date()
      });

      this._formService.hideFormMsg("default-towing-limit-error-message");
      this.rowData = null;
      this.forms.defaultTowingLimitForm.get('effDateFrom')?.enable();
    },
    resetFilterLovForm: () => {
      this.forms.filterLovForm.reset();
    },
    resetAllForms: () => {
      this.forms.resetFilterLovForm();
      this.forms.resetDefaultTowingLimitForm();
    }
  }

  private defaultTowingLimitFormManipulate = {
    dbToForm: (data: any) => {
      return {
        lineCd: data.LINE_CD,
        sublineCd: data.SUBLINE_CD,
        clientCd: data.CLIENT_CD,
        towingAmt: parseFloat(data.TOWING_AMT).toFixed(2),
        active: data.ACTIVE_TAG,
        effDateFrom: data.EFF_DATE_FROM,
        effDateTo: data.EFF_DATE_TO,
        remarks: data.REMARKS,
        lastUpdateUser: data.USER_ID,
        lastUpdate: data.LAST_UPDATE,
      };
    },
    formToDb: (data: any) => {
      return {
        LINE_CD: data.lineCd,
        SUBLINE_CD: data.sublineCd,
        CLIENT_CD: data.clientCd,
        TOWING_AMT: parseFloat(this._formService.setToNumberWithDecimal(data.towingAmt)),
        ACTIVE_TAG: data.active,
        EFF_DATE_FROM: data.effDateFrom ? this.datePipe.transform(data.effDateFrom, 'yyyy-MM-dd') : null,
        EFF_DATE_TO: data.effDateTo ? this.datePipe.transform(data.effDateTo, 'yyyy-MM-dd') : null,
        REMARKS: data.remarks,
        USER_ID: null,
        LAST_UPDATE: null,
      };
    },
  }

  public defaultTowingLimitTblConfig: any = {
    cols: [
      {
        key: "TOWING_AMT",
        header: "Towing Amount",
        dataType: "currency",
      },
      {
        key: "EFF_DATE_FROM",
        header: "Effective Date",
        dataType: "date"
      },
      {
        key: "EFF_DATE_TO",
        header: "Expiry Date",
        dataType: "date"
      },
      {
        key: "ACTIVE_TAG",
        header: "A",
        dataType: "checkbox",
        width: '48px',
      }
    ],
    tblData: [],
    selection: "single",
    paginator: true,
    rowsPerPage: 10,
    lazy: false,
    totalRecords: 15,
    loading: false,
  }

  ngOnInit(): void {
    this.minExpDate = new Date();
    this.valueChangesSubscriptions.add(this.forms.defaultTowingLimitForm.get('effDateFrom')?.valueChanges.subscribe((res) => {
      this.minExpDate = res ? new Date(res) : undefined;
    }));
    this.valueChangesSubscriptions.add(this.forms.defaultTowingLimitForm.get('effDateTo')?.valueChanges.subscribe((res) => {
      this.maxEffDate = res ? new Date(res) : undefined;
    }));
  }

  ngOnDestroy(): void {
    this.valueChangesSubscriptions.unsubscribe();
  }

  public openLov = {
    subline: () => {
      const LOVOPTIONS: { [key: string]: any } = {
        disableClose: true,
        width: '512px',
        data: {
          lineCd: 'MC',
          table: 'SUBLINE',
          moduleId: this.moduleId
        },
      }
      this._matDialog.open(LineLovDialogComponent, LOVOPTIONS).afterClosed().subscribe((dataOut: any) => {
        let output = dataOut.content;
        if (dataOut.button === 'OK') {
          this.forms.resetDefaultTowingLimitForm();
          if (output) {
            this.forms.filterLovForm.patchValue({
              lineCd: output.LINE_CD,
              sublineCd: output.SUBLINE_CD,
              sublineName: output.SUBLINE_NAME,
              sublineCdDisp: `${output.SUBLINE_CD} - ${output.SUBLINE_NAME}`,
            });
          }
          else {
            this.forms.filterLovForm.patchValue({
              lineCd: null,
              sublineCd: null,
              sublineName: null,
              sublineCdDisp: null,
            });
            this.btns.defaultTowingLimit.addDisabled = true;
          }
          this.getData.defaultTowingLimit();
        }
      });
    },
    clientType: () => {
      const LOVOPTIONS: { [key: string]: any } = {
        disableClose: true,
        width: '512px',
        data: {
          table: 'CLIENT_GROUP',
          moduleId: this.moduleId,
        },
      };
      this._matDialog.open(ClientGroupLovDialog, LOVOPTIONS).afterClosed().subscribe((dataOutput: any) => {
        let output = dataOutput.content;
        if (dataOutput.option?.toUpperCase() !== "BACK") {
          this.forms.resetDefaultTowingLimitForm();
          if (output.CLIENT_CD) {
            this.forms.filterLovForm.patchValue({
              clientCd: output.CLIENT_CD,
              clientName: output.CLIENT_DESC,
              clientCdDisp: `${output.CLIENT_CD} - ${output.CLIENT_DESC}`,
            });
          }
          else {
            this.forms.filterLovForm.patchValue({
              clientCd: null,
              clientName: null,
              clientCdDisp: null,
            });
            this.btns.defaultTowingLimit.addDisabled = true;
          }
          this.getData.defaultTowingLimit();
        }
      });
    }
  };

  private getData = {
    defaultTowingLimit: () => {
      try {
        this.defaultTowingLimitTblConfig.tblData = [];
        if (this.forms.filterLovForm.get('lineCd')?.value
          && this.forms.filterLovForm.get('sublineCd')?.value
          && this.forms.filterLovForm.get('clientCd')?.value) {
          this._jDS.contorlLoading(true);
          this.defaultTowingLimitTblConfig.loading = true;
          this._api.getTowingAmtPerClient({
            lineCd: this.forms.filterLovForm.get('lineCd')?.value,
            sublineCd: this.forms.filterLovForm.get('sublineCd')?.value,
            clientCd: this.forms.filterLovForm.get('clientCd')?.value,
            moduleId: this.moduleId,
            userId: this._userDetailService.userId,
            type: "MODULE",
          }).subscribe({
            next: (response: any) => {
              this._securityService.checkRequestKeyResponse(response, () => {
                this._securityService.hasValidCsrfToken(response, () => {
                  if (response.status === "SUCCESS") {
                    let content = JSON.parse(this._jDS.decrypt(response?.response));
                    this.defaultTowingLimitTblConfig.tblData = content.data;
                    this._jDS.contorlLoading(false);
                    this.defaultTowingLimitTblConfig.loading = false;
                    this.btns.defaultTowingLimit.addDisabled = false;
                  }
                  else {

                    this._jDS.contorlLoading(false);
                    this.defaultTowingLimitTblConfig.loading = false;
                    this.btns.defaultTowingLimit.addDisabled = true;
                  }
                });
              });
            },
            error: (e: any) => {
              this._securityService.checkRequestKeyResponse(e, () => {

                this._jDS.contorlLoading(false);
                this.defaultTowingLimitTblConfig.loading = false;
                this.btns.defaultTowingLimit.addDisabled = true;
              });
            }
          });
        }
        else {
          this.defaultTowingLimitTblConfig.loading = false;
          this.btns.defaultTowingLimit.addDisabled = true;
        }
      }
      catch (e) {
    console.error(e);

      }
    },
  };

  private addDay(date: Date, day: number): Date {
    date = new Date(date);
    date.setDate(date.getDate() + day);
    return date;
  }

  public selections = {
    activeTypes: [
      { cd: 'A', name: 'Yes' },
      { cd: 'I', name: 'No' },
    ],
  };

  public onRowClick(ev: any) {
    try {
      if (ev) {
        this.rowData = ev;
        this.forms.defaultTowingLimitForm.get('effDateFrom')?.disable();
        this.populateFormFromTable();
      }
      else {
        this.rowData = null;
        this.forms.resetDefaultTowingLimitForm();
      }
    }
    catch (e) {
    console.error(e);
      this.rowData = null;
      this.forms.resetDefaultTowingLimitForm();
    }
  }

  private populateFormFromTable() {
    try {
      let data = this.rowData;
      this.btns.defaultTowingLimit.updateDisabled = false;
      this.btns.defaultTowingLimit.deleteDisabled = !(this.rowData?.operation === 'I');
      this.forms.defaultTowingLimitForm.patchValue(this.defaultTowingLimitFormManipulate.dbToForm(data));
    }
    catch (e) {
    console.error(e);

    }
  }

  public onAdd(): void {
    try {
      this._formService.hideFormMsg("default-towing-limit-error-message");
      if (this.forms.defaultTowingLimitForm.valid) {
        this.forms.defaultTowingLimitForm.patchValue({
          lineCd: this.forms.filterLovForm.get('lineCd')?.value,
          sublineCd: this.forms.filterLovForm.get('sublineCd')?.value,
          clientCd: this.forms.filterLovForm.get('clientCd')?.value,
        });
        let rowToBeAdded: { [key: string]: any } = this.defaultTowingLimitFormManipulate.formToDb(this.forms.defaultTowingLimitForm.getRawValue());
        rowToBeAdded.operation = 'I';
        this.defaultTowingLimitTblConfig.tblData = [rowToBeAdded, ...this.defaultTowingLimitTblConfig.tblData];
        this.forms.resetDefaultTowingLimitForm();
        this.btns.defaultTowingLimit.saveDisabled = false;
      }
      else {
        this.showErrorValidator.defaultTowingLimit();
      }
    }
    catch (e) {
    console.error(e);

    }
  }

  public onUpdate(): void {
    try {
      this._formService.hideFormMsg("default-towing-limit-error-message");
      if (this.forms.defaultTowingLimitForm.valid) {
        const indexOfRow = this.defaultTowingLimitTblConfig.tblData.indexOf(this.rowData);
        let rowToBeUpdated: { [key: string]: any } = this.defaultTowingLimitFormManipulate.formToDb(this.forms.defaultTowingLimitForm.getRawValue());
        rowToBeUpdated.operation = (this.rowData.operation === 'I') ? 'I' : 'U';
        this.forms.resetDefaultTowingLimitForm();
        this.defaultTowingLimitTblConfig.tblData[indexOfRow] = rowToBeUpdated;
        this.selectedRow = [];
        this.btns.defaultTowingLimit.saveDisabled = false;
        this.defaultTowingLimitTblConfig.tblData = [...this.defaultTowingLimitTblConfig.tblData];
      }
      else {
        this.showErrorValidator.defaultTowingLimit();
      }
    }
    catch (e) {
    console.error(e);

    }
  }

  public onDelete(): void {
    try {
      this.defaultTowingLimitTblConfig.tblData = this.defaultTowingLimitTblConfig.tblData.filter((row: any) => {
        return row !== this.rowData;
      });
      this.forms.resetDefaultTowingLimitForm();
      this.btns.defaultTowingLimit.saveDisabled = false;
    }
    catch (e) {
    console.error(e);

    }
  }

  public onSave(): void {
    try {
      this._jDS.contorlLoading(true);
      this.showForm = false;
      this._formService.showFormLoader(null, "default-towing-limit-maintenance-loading", "Saving.<br>Please wait ...", null, null, true);
      let toBeSaved = this.defaultTowingLimitTblConfig.tblData.filter((row: any) => ['I', 'U'].includes(row.operation));
      if (toBeSaved.length > 0) {
        this._api.saveTowingAmtPerClient(toBeSaved).subscribe({
          next: (response: any) => {
            this._securityService.checkRequestKeyResponse(response, () => {
              this._securityService.hasValidCsrfToken(response, () => {
                if (response.status === 'SUCCESS') {
                  this.onComplete.defaultTowingLimit();
                }
                else {
                  this.onComplete.defaultTowingLimitError();
                }
              });
            });
          },
          error: (data: any) => {
            this._securityService.checkRequestKeyResponse(data, () => {

              this.onComplete.defaultTowingLimitError();
            });
          }
        });
      }
      else {
        this.onComplete.defaultTowingLimit();
      }
    }
    catch (e) {
    console.error(e);

    }
  }

  private onComplete = {
    defaultTowingLimit: () => {
      this._jDS.contorlLoading(false);
      this._formService.hideFormLoader("default-towing-limit-maintenance-loading");
      this.openDialog('SUCCESS', this.moduleName.replace('Maintenance', ''), 'saved');
      this.btns.defaultTowingLimit.saveDisabled = true;
      this.showForm = true;
      this.forms.resetDefaultTowingLimitForm();
    },
    defaultTowingLimitError: () => {
      this._jDS.contorlLoading(false);
      this.openDialog('FAILED', this.moduleName.replace('Maintenance', ''), 'saving');
      this._formService.hideFormLoader("default-towing-limit-maintenance-loading");
      this.showForm = true;
      this.forms.resetDefaultTowingLimitForm();
    },
  }

  private openDialog(title: string, contentTitle: string, contentDetail: string) {
    try {
      this._matDialog.open(DialogMsgComponent, {
        disableClose: true,
        width: '512px',
        data: {
          title: title,
          content:
            title === 'SUCCESS'
              ? `${contentTitle} details successfully ${contentDetail}!`
              : `${contentTitle} details ${contentDetail} failed!`,
        },
      }).afterClosed().subscribe(() => {
        if (title === 'SUCCESS') {
          this.getData.defaultTowingLimit();
        }
      });
    }
    catch (e) {
    console.error(e);

    }
  }

  //* Validator Stuffs Down here */

  private checkDateValid(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const effDateValue = control.get('effDateFrom')?.value ? this.datePipe.transform(control.get('effDateFrom')?.value, 'yyyy-MM-dd') : null;
      const expDateValue = control.get('effDateTo')?.value ? this.datePipe.transform(control.get('effDateTo')?.value, 'yyyy-MM-dd') : null;
      if (!effDateValue) {
        return null;
      }
      let hasConcurrent: boolean = this.defaultTowingLimitTblConfig?.tblData.filter((d: any) => {
        return this.checkBool(d.EFF_DATE_FROM, d.EFF_DATE_TO, effDateValue, expDateValue);
      }).length > 0 ? true : false;
      return hasConcurrent ? { hasConcurrent: true } : null;
    }
  }

  private checkDecimalsExceeded(digits: number, decimals: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value && parseFloat(this._formService.setToNumberWithDecimal(control.value));
      if (!value) return null;
      let isExceeded: boolean = value > +"".padEnd(digits, '9') + +('.' + ''.padEnd(decimals, '9'))
      return isExceeded ? { hasDecimalsExceeded: true } : null;
    }
  }

  private checkBool(dataEffDate: string, dataExpDate: string | null, effDateVal: string, expDateVal: string | null): boolean {
    if (!this.rowData) {
      let dateOverlapBool = this.dateOverlapBool1(dataEffDate, dataExpDate, effDateVal, expDateVal);
      let dateOverlapBool2 = this.dateOverlapBool2(dataEffDate, dataExpDate, effDateVal, expDateVal);
      return dateOverlapBool || dateOverlapBool2;
    }
    let dateOverlapBool1 = this.dateOverlapBool1(dataEffDate, dataExpDate, this.rowData.EFF_DATE_FROM, this.rowData.EFF_DATE_FROM);
    let dateOverlapBool2 = this.dateOverlapBool2(dataEffDate, dataExpDate, this.rowData.EFF_DATE_FROM, this.rowData.EFF_DATE_FROM);
    let dateOverlapBool3 = this.dateOverlapBool1(dataEffDate, dataExpDate, effDateVal, expDateVal);
    let dateOverlapBool4 = this.dateOverlapBool2(dataEffDate, dataExpDate, effDateVal, expDateVal);
    return !(dateOverlapBool1 || dateOverlapBool2) && (dateOverlapBool3 || dateOverlapBool4);
  }

  private dateOverlapBool1(dataEffDate: string, dataExpDate: string | null, effDateVal: string, expDateVal: string | null): boolean {
    let res = (new Date(effDateVal) >= new Date(dataEffDate) && new Date(effDateVal) <= new Date(dataExpDate ?? effDateVal))
    return res;
  }

  private dateOverlapBool2(dataEffDate: string, dataExpDate: string | null, effDateVal: string, expDateVal: string | null): boolean {
    let res = (new Date(dataEffDate) <= new Date(expDateVal ?? dataEffDate) && new Date(effDateVal) <= new Date(dataExpDate ?? dataEffDate))
    return res;
  }

  public showErrorValidator = {
    defaultTowingLimit: () => {
      try {
        if (this.forms.defaultTowingLimitForm.errors) {
          const controlError = this.forms.defaultTowingLimitForm.errors;
          Object.keys(controlError).forEach(keyError => {
            if (keyError == 'hasConcurrent' && controlError[keyError] === true)
              this.errorMessage = "Unable to add record. Date of Effectivity is concurrent with another record. Please choose a different range of Date of Effectivity.";
          });
        }
        else if (this.forms.defaultTowingLimitForm.get('effDateTo')?.errors || this.forms.defaultTowingLimitForm.get('effDateFrom')?.errors) {
          this.errorMessage = "Invalid Effective/Expiry Date. Please input valid dates.";
        }
        else {
          Object.keys(this.forms.defaultTowingLimitForm.controls).forEach(key => {
            const controlErrors = this.forms.defaultTowingLimitForm.get(key)?.errors;
            if (controlErrors != null) {
              Object.keys(controlErrors).forEach(keyError => {
                if (keyError == 'matDatepickerMin' && key == 'effDateTo')
                  this.errorMessage = "Expiry Date must not be earlier than Effective Date.";
                else if (keyError == 'hasDecimalsExceeded' && controlErrors[keyError] === true)
                  this.errorMessage = "Invalid value in Towing Amount. The value entered exceeded the maximum limit.";
                else if (keyError == 'required' && controlErrors[keyError] === true)
                  this.errorMessage = "There are missing information. Please provide necessary information needed.";
              });
            }
          });
        }
        this._formService.showFormMsg("default-towing-limit-error-message", this.errorMessage, "E");
      }
      catch (e) {
    console.error(e);

      }
    },
  }

  public allowNumericDigitsOnly(event: any) {
    if (/[0-9.]/.test(String.fromCharCode(event.keyCode))) {
      return true;
    }
    else {
      event.preventDefault();
      return false;
    }
  }

}
